import { AuthType, AuthStatus, init as thoughtSpotSdkInit, logout as thoughtSpotSdkLogout } from '@thoughtspot/visual-embed-sdk';
import { isFalsy } from '../lib/Helpers';
import { fetchData } from './Axios';
import { showToast } from './Notifications';

/* LaunchDarkly ClientIDs for QA/Prod */
const launchDarklyEnvironments = {
  production: '5b92e71964e7b428ec862be2',
  test: '5b92e71964e7b428ec862be1',
};

const defaultState = {
  user: {},
  account: { id: '', sid: '' },
  accounts: [],
  brand: { id: '', sid: '' },
  brands: [],
  applicationError: {
    defaultErrorPayload: {
      componentStack: [],
      message: '',
      stack: [],
    },
    errorPayload: {
      componentStack: [],
      message: '',
      stack: [],
    },
    state: false,
  },
  featureFlags: {},
  launchDarklyEnv: '',
  loading: false,
  dataLoading: true,
  sidebarEnabled: true,
  thoughtSpotInitialized: false,
  thoughtSpotSession: null,
  thoughtSpotAccessError: false,
  thoughtSpotInitSdkError: {
    brandId: '',
    isError: false,
  },
};

const USER_AUTHENTICATION = 'USER_AUTHENTICATION';
const AUTHORIZATION_DATA_LOADING = 'AUTHORIZATION_DATA_LOADING';

export const authorization = ({ user, url }) => {
  return async (dispatch) => {
    const { accountSid, brandSid } = parseUrlParams(url);
    await dispatch({ type: USER_AUTHENTICATION, payload: user });

    if (accountSid && accountSid !== 'administration' && brandSid) {
      const account = await dispatch(fetchData(`/accounts/sid/${accountSid}`))
        .catch((e) => {
          dispatch(showToast({
            state: 'ERROR',
            title: 'Error',
            message: 'Issue retrieving accounts. Please try again later. Please try again later',
            dismissible: true,
            dismissAfter: 1500,
          }));
          return defaultState.account;
        });

      await dispatch(setAccount(account));

      const brands = await dispatch(fetchData(`/accounts/${account.id}/brands`))
        .catch((e) => {
          dispatch(showToast({
            state: 'ERROR',
            title: 'Error',
            message: 'Issue retrieving brands. Please try again later',
            dismissible: true,
            dismissAfter: 1500,
          }));
          return defaultState.brands;
        });

      await dispatch(setBrands(brands));

      const brand = await dispatch(fetchData(`/brands/sid/${brandSid}`))
        .catch((e) => {
          dispatch(showToast({
            state: 'ERROR',
            title: 'Error',
            message: 'Issue retrieving brand. Please try again later. Please try again later',
            dismissible: true,
            dismissAfter: 1500,
          }));
          return defaultState.brand;
        });

      await dispatch(setBrand(brand));
    }
    dispatch({ type: AUTHORIZATION_DATA_LOADING, payload: false });
  };
};

export const SET_ACCOUNT = 'SET_ACCOUNT';
export const setAccount = (account) => ({
  type: SET_ACCOUNT,
  payload: account,
});

export const SET_BRAND = 'SET_BRAND';
export const setBrand = (brand) => {
  return async (dispatch, getState) => {
    dispatch({ type: SET_BRAND, payload: brand });
  };
};

export const SET_ACCOUNTS = 'SET_ACCOUNTS';
export const setAccounts = (accounts) => ({
  type: SET_ACCOUNTS,
  payload: accounts,
});

export const SET_BRANDS = 'SET_BRANDS';
export const setBrands = (brands) => ({
  type: SET_BRANDS,
  payload: brands,
});

const CLEAR_ACCOUNT_AND_BRAND = 'CLEAR_ACCOUNT_AND_BRAND';
export const clearAccountAndBrand = () => ({
  type: CLEAR_ACCOUNT_AND_BRAND,
});

/**
 * Builds LaunchDarkly user context object
 * @param {Object} user
 * @return {Object} LaunchDarkly context
 */
const getLaunchDarklyUserContext = (user) => {
  if (!user) return undefined;
  return {
    key: user?.email,
    email: user?.email,
    name: user?.name,
    roles: user?.user_roles,
  };
};

/**
 * Builds LaunchDarkly account context object
 * @param {Object} account
 * @return {Object} LaunchDarkly context
 */
const getLaunchDarklyAccountContext = (account) => {
  if (!account?.id) return undefined;
  return {
    key: account.sid,
    sid: account.sid,
    name: account.name,
  };
};

/**
 * Builds LaunchDarkly brand context object
 * @param {Object} brand
 * @return {Object} LaunchDarkly context
 */
const getLaunchDarklyBrandContext = (brand) => {
  if (!brand?.id) return undefined;
  return {
    key: brand.sid,
    sid: brand.sid,
    name: brand.name,
    category: brand.category,
    // eslint-disable-next-line camelcase
    client_type: brand.client_type,
  };
};

/**
 * Builds combined LaunchDarkly multi-context object
 * @param {Object} user
 * @param {Object} account
 * @param {Object} brand
 * @return {Object} LaunchDarkly context
 */
export const getLaunchDarklyContext = ({ user, account, brand }) => {
  return {
    kind: 'multi',
    user: getLaunchDarklyUserContext(user),
    account: getLaunchDarklyAccountContext(account),
    brand: getLaunchDarklyBrandContext(brand),
  };
};

export const SET_FEATURE_FLAGS = 'SET_FEATURE_FLAGS';
/**
 * Set Feature Flags for Current Environment (Production/Test)
 * @param {Object} featureFlags -- All feature flags and their values
 * @return {Function} Dispatch of SET_FEATURE_FLAGS payload
 */
export const setFeatureFlags = (featureFlags) => {
  return async (dispatch, getState) => {
    const currentEnvironment = window?.location?.href?.includes('cloud.bridg.com')
      ? launchDarklyEnvironments.production
      : launchDarklyEnvironments.test;

    return dispatch({
      type: SET_FEATURE_FLAGS,
      payload: {
        launchDarklyEnv: currentEnvironment,
        featureFlags,
      },
    });
  };
};

/**
 * Parses URL Params to Search for AccountSid and BrandSid
 * @param {string} urlString -- HREF String
 * @return {Object} @prop accountSid, @prop brandSid
 */
export const parseUrlParams = (urlString) => {
  const url = new URL(urlString);
  const params = url?.pathname?.split('/');
  return {
    accountSid: params[1],
    brandSid: params[2],
  };
};

export const TOGGLE_GLOBAL_LOADING = 'TOGGLE_GLOBAL_LOADING';
/**
 * Toggles Global Loading Flag
 * @param {boolean} bool
 * @return {Object} TOGGLE_GLOBAL_LOADING Payload
 */
export const toggleGlobalLoading = (bool) => ({
  type: TOGGLE_GLOBAL_LOADING,
  payload: bool,
});

export const SET_ACCOUNT_FROM_BRAND_SWITCHER = 'SET_ACCOUNT_FROM_BRAND_SWITCHER';
/**
 * Handler given to BrandSwitcher to accept the Account Payload & Update App State
 * @param {Object} account - Account Payload from BrandSwitcher
 * @return {Object} -- SET_ACCOUNT_FROM_BRAND_SWITCHER payload
 */
export const setAccountFromBrandSwitcher = (account) => {
  return {
    type: SET_ACCOUNT_FROM_BRAND_SWITCHER,
    payload: account,
  };
};

export const SET_BRAND_FROM_BRAND_SWITCHER = 'SET_BRAND_FROM_BRAND_SWITCHER';
/**
 * Handler given to BrandSwitcher to accept the Brand Payload & Update App state
 * @param {Object} brand -- Brand Payload from BrandSwitcher
 * @return {Object} -- SET_BRAND_FROM_BRAND_SWITCHER payload
 */
export const setBrandFromBrandSwitcher = (brand) => {
  return async (dispatch, getState) => {
    dispatch({ type: SET_BRAND_FROM_BRAND_SWITCHER, payload: brand });
  };
};

export const SET_BRANDS_FROM_BRAND_SWITCHER = 'SET_BRANDS_FROM_BRAND_SWITCHER';
/**
 * Handler Passed to BrandSwitcher to set all possible brands for the account when selecting
 * @param {Array.<Object>} data
 * @param {string} id
 * @return {Object} SET_BRANDS_FROM_BRAND_SWITCHER Payload
 */
export const setBrandsFromBrandSwitcher = (data, id) => {
  const account = data.filter((accnt) => accnt.id === id);
  return {
    type: SET_BRANDS_FROM_BRAND_SWITCHER,
    payload: account.length
      ? account[0].brands
      : [],
  };
};

export const SET_BRAND_FROM_SIDEBAR_SWITCHER = 'SET_BRAND_FROM_SIDEBAR_SWITCHER';
/**
 * Toggles Current Brand Within an Account from the BrandSwitcher
 * @param {Object} brand
 * @return {string}
 */
export const setBrandFromSidebarSwitcher = (brand) => {
  return async (dispatch, getState) => {
    dispatch(toggleGlobalLoading(true));
    const { account } = getState().app;
    dispatch({ type: SET_BRAND_FROM_SIDEBAR_SWITCHER, payload: brand });
    /* Create waiting effect when new brand selected. Change is instant otherwise. */
    setTimeout(() => {
      dispatch(toggleGlobalLoading(false));
    }, 2000);
    return `/${account.sid}/${brand.sid}`;
  };
};

export const TOGGLE_APPLICATION_ERROR_STATE = 'TOGGLE_APPLICATION_ERROR_STATE';
/**
 * Toggles Application Error State Flag
 * @param {boolean} state
 * @param {Object} errorPayload
 * @return {Object} toggleApplicationErrorState
 */
export const toggleApplicationErrorState = (state, errorPayload = {}) => {
  return {
    type: TOGGLE_APPLICATION_ERROR_STATE,
    payload: {
      state,
      errorPayload: isFalsy(errorPayload)
        ? { ...defaultState.applicationError.errorPayload }
        : errorPayload,
      defaultErrorPayload: { ...defaultState.applicationError.errorPayload },
    },
  };
};

export const THOUGHTSPOT_LOGOUT = 'THOUGHTSPOT_LOGOUT';
export const thoughtSpotLogout = () => {
  return async (dispatch) => {
    try {
      await thoughtSpotSdkLogout();
    // eslint-disable-next-line no-unused-vars
    } catch (e) {
      // console.error('Explorer Logout Error:', e); // Uncomment for debugging
      // No-op -- if logout fails, we already no longer have valid credentials
    }
    return dispatch({ type: THOUGHTSPOT_LOGOUT });
  };
};

export const THOUGHTSPOT_INIT = 'THOUGHTSPOT_INIT';
export const thoughtSpotInit = (brand) => {
  return async (dispatch, getState) => {
    dispatch(setThoughtSpotInitSdkError({ brandId: '', isError: false }));
    thoughtSpotSdkInit({
      thoughtSpotHost: window?.location?.hostname === 'cloud.bridg.com'
        ? 'https://explorer.bridg.com'
        : 'https://towerbridg.thoughtspot.cloud',
      authType: AuthType.TrustedAuthTokenCookieless,
      getAuthToken: () => dispatch(thoughtSpotAuthorization(brand)),
      autoLogin: true,
      disableLoginRedirect: true,
      loginFailedMessage: `<div class="explorer-auth-failed-message">Login failed or expired. Please refresh and try again.
      <br /><br />If issue perisists, this feature may not be enabled for your account.
      <br /><br />Contact your Client Partner for further assistance or to learn more about Bridg's real-time data exploration tools.</div>`,
    }).on(AuthStatus.FAILURE, (failureType) => {
      console.error('Explorer AuthStatus.FAILURE:', failureType);
      if (failureType === 'SDK') dispatch(setThoughtSpotInitSdkError({ brandId: brand?.id, isError: true }));
    // }).on(AuthStatus.SUCCESS, (sessionInfo) => {
    //   console.log('Explorer AuthStatus.SUCCESS:', getState().app?.thoughtSpotSession?.brand?.name, sessionInfo);
    // }).on(AuthStatus.LOGOUT, () => {
    //   console.log('Explorer AuthStatus.LOGOUT');
    });
    dispatch({ type: THOUGHTSPOT_INIT, payload: true });
  };
};

export const THOUGHTSPOT_AUTHORIZATION = 'THOUGHTSPOT_AUTHORIZATION';
export const thoughtSpotAuthorization = (brand) => {
  return async (dispatch, getState) => {
    const { user } = getState().app;
    if (brand?.id) {
      const res = await dispatch(fetchData(`/users/${user.bridg_user_id}/integrations/thoughtspot/login-token?brandId=${brand.id}`))
        .catch((e) => {
          console.error('Failed to get Explorer access token:', e);
          if (e?.response?.status === 403) dispatch(setThoughtSpotAccessError(true));
          dispatch({ type: THOUGHTSPOT_AUTHORIZATION, payload: defaultState.thoughtSpotSession });
          dispatch(showToast({
            state: 'ERROR',
            title: 'Error',
            message: 'Issue retrieving Explorer access token. Please try again later.',
            dismissible: true,
            dismissAfter: 5000,
          }));
          return null; // Return null for token
        });
      const { token } = res;
      const payload = token ? { token, user, brand } : defaultState.thoughtSpotSession;
      dispatch({ type: THOUGHTSPOT_AUTHORIZATION, payload });
      dispatch(setThoughtSpotAccessError(false));
      return token;
    } return Promise.reject(new Error('Brand ID is required'));
  };
};

export const THOUGHTSPOT_ACCESS_ERROR = 'THOUGHTSPOT_ACCESS_ERROR';
export const setThoughtSpotAccessError = (payload) => {
  return { type: THOUGHTSPOT_ACCESS_ERROR, payload };
};

export const THOUGHTSPOT_INIT_SDK_ERROR = 'THOUGHTSPOT_INIT_SDK_ERROR';
export const setThoughtSpotInitSdkError = (payload) => {
  return { type: THOUGHTSPOT_INIT_SDK_ERROR, payload };
};

export const actions = {
  authorization,
  clearAccountAndBrand,
  parseUrlParams,
  setAccountFromBrandSwitcher,
  setBrandFromBrandSwitcher,
};

export default function reducer(state = defaultState, action = undefined) {
  switch (action.type) {
    case USER_AUTHENTICATION:
      return { ...state, user: action.payload };
    case SET_ACCOUNTS:
      return { ...state, accounts: action.payload };
    case SET_BRANDS:
      return { ...state, brands: action.payload };
    case SET_ACCOUNT_FROM_BRAND_SWITCHER:
      return { ...state, account: action.payload };
    case SET_BRAND_FROM_BRAND_SWITCHER:
      return { ...state, brand: action.payload };
    case SET_BRAND_FROM_SIDEBAR_SWITCHER:
      return { ...state, brand: action.payload };
    case SET_BRANDS_FROM_BRAND_SWITCHER:
      return { ...state, brands: action.payload };
    case SET_FEATURE_FLAGS:
      return {
        ...state,
        featureFlags: action.payload.featureFlags,
        launchDarklyEnv: action.payload.launchDarklyEnv,
      };
    case CLEAR_ACCOUNT_AND_BRAND:
      return {
        ...state,
        account: defaultState.account,
        brand: defaultState.brand,
        brands: defaultState.brands,
      };
    case TOGGLE_APPLICATION_ERROR_STATE:
      return { ...state, applicationError: action.payload };
    case TOGGLE_GLOBAL_LOADING:
      return { ...state, loading: action.payload };
    case AUTHORIZATION_DATA_LOADING:
      return { ...state, dataLoading: action.payload };
    case SET_ACCOUNT:
      return { ...state, account: action.payload };
    case SET_BRAND:
      return { ...state, brand: action.payload };
    case THOUGHTSPOT_AUTHORIZATION:
      return { ...state, thoughtSpotSession: action.payload };
    case THOUGHTSPOT_ACCESS_ERROR:
      return { ...state, thoughtSpotAccessError: action.payload };
    case THOUGHTSPOT_INIT_SDK_ERROR:
      return { ...state, thoughtSpotInitSdkError: action.payload };
    case THOUGHTSPOT_INIT:
      return { ...state, thoughtSpotInitialized: action.payload };
    case THOUGHTSPOT_LOGOUT:
      return { ...state,
        thoughtSpotSession: defaultState.thoughtSpotSession,
        thoughtSpotInitialized: false,
      };
    default:
      return state;
  }
}
